import {
	resolveExternalGeometrySourceRefEntityId,
} from './ExternalGeometrySourceDescriptor';
import type { ExternalGeometrySourceDescriptor } from './ExternalGeometrySourceDescriptor';
import type { SketchEntityReferenceSelectionIntentRecordSnapshot } from './SketchEntityReferenceSelectionIntentRecordSnapshot';
import type { ExecutionDiagnostic } from './SketchEntityReferenceDiagnostics';
import type {
	ArcProjectionResult,
	CircleProjectionResult,
	EllipseArcProjectionResult,
	EllipseProjectionResult,
	LineProjectionResult,
	ParabolaProjectionResult,
	ProjectionResult,
	SplineProjectionResult,
	StyleSplineProjectionResult,
} from './ProjectionResult';
import type { SketchReferenceStatus } from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceStatus';
import type {
	SketchEntityReferenceBoundaryMemberState,
	SketchReferenceDriveMode,
	SketchReferenceRelationSemantics,
	LineReferenceRebuildPayload,
	LineReferenceProjectionAttachmentMode,
	LineReferenceProjectionState,
	CircleReferenceRebuildPayload,
	EllipseReferenceRebuildPayload,
	ArcReferenceRebuildPayload,
	ArcReferenceProjectionState,
	EllipseArcReferenceRebuildPayload,
	ParabolaReferenceRebuildPayload,
	SplineReferenceRebuildPayload,
	StyleSplineReferenceRebuildPayload,
	EllipseArcReferenceProjectionState,
	SketchEntityReferenceRebuildPayload,
	SketchEntityReferenceProjectionState,
	SketchEntityReferenceRecord,
	ReferenceDrivenRelationRuntimeRecord,
	ReferenceDrivenLineRelationRuntimeRecord,
	ReferenceDrivenCircleRelationRuntimeRecord,
	ReferenceDrivenEllipseRelationRuntimeRecord,
	ReferenceDrivenArcRelationRuntimeRecord,
	ReferenceDrivenEllipseArcRelationRuntimeRecord,
	ReferenceDrivenParabolaRelationRuntimeRecord,
	ReferenceDrivenSplineRelationRuntimeRecord,
	ReferenceDrivenStyleSplineRelationRuntimeRecord,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRecord';

export type {
	SketchReferenceDriveMode,
	SketchReferenceRelationSemantics,
	LineReferenceRebuildPayload,
	LineReferenceProjectionAttachmentMode,
	LineReferenceProjectionState,
	CircleReferenceRebuildPayload,
	EllipseReferenceRebuildPayload,
	ArcReferenceRebuildPayload,
	ArcReferenceProjectionState,
	EllipseArcReferenceRebuildPayload,
	ParabolaReferenceRebuildPayload,
	SplineReferenceRebuildPayload,
	StyleSplineReferenceRebuildPayload,
	EllipseArcReferenceProjectionState,
	SketchEntityReferenceRebuildPayload,
	SketchEntityReferenceProjectionState,
	SketchEntityReferenceRecord,
	ReferenceDrivenRelationRuntimeRecord,
	ReferenceDrivenLineRelationRuntimeRecord,
	ReferenceDrivenCircleRelationRuntimeRecord,
	ReferenceDrivenEllipseRelationRuntimeRecord,
	ReferenceDrivenArcRelationRuntimeRecord,
	ReferenceDrivenEllipseArcRelationRuntimeRecord,
	ReferenceDrivenParabolaRelationRuntimeRecord,
	ReferenceDrivenSplineRelationRuntimeRecord,
	ReferenceDrivenStyleSplineRelationRuntimeRecord,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRecord';

export type { SketchReferenceStatus } from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceStatus';
export type { SketchEntityReferenceBoundaryMemberState } from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRecord';

/**
 * 判断引用记录是否处于正常驱动状态。
 *
 * @param reference 引用状态载体。
 * @returns 处于 active 状态时返回 true。
 */
export function isActiveSketchEntityReferenceRecord(
	reference: Pick<
		SketchEntityReferenceRecord,
		'status' | 'boundaryMemberState'
	>,
): boolean {
	return reference.status === 'active'
		&& resolveSketchEntityReferenceBoundaryMemberState(reference) !== 'detached';
}

/**
 * 读取面/环集合成员状态。
 *
 * @param reference 集合成员状态载体。
 * @returns 新字段、旧字段或缺省 active。
 */
export function resolveSketchEntityReferenceBoundaryMemberState(
	reference: Pick<
		SketchEntityReferenceRecord,
		'boundaryMemberState'
	>,
): SketchEntityReferenceBoundaryMemberState {
	return reference.boundaryMemberState ?? 'active';
}

/**
 * 判断引用身份是否仍需被目标占用和生命周期查询保留。
 *
 * dangling 只停止正常驱动，不会把目标转换成可重新绑定的空闲实体。
 * failed 只表示上一次求值失败；在记录被明确删除前，它仍是可恢复的依赖边，
 * 必须继续占用目标并参与循环检测，避免恢复时生成重复绑定或依赖环。
 *
 * @param reference 引用状态载体。
 * @returns active、dangling 或 failed 时返回 true。
 */
export function isRetainedSketchEntityReferenceRecord(
	reference: Pick<SketchEntityReferenceRecord, 'status'>,
): boolean {
	return reference.status === 'active'
		|| reference.status === 'dangling'
		|| reference.status === 'failed';
}

/**
 * 判断引用记录是否属于可由来源草图变更重新求值的引用。
 *
 * failed 只表示上一次求值失败，不表示依赖关系已经被删除。只要记录仍在仓储中，
 * 后续权威来源变更就应再次求值，使临时缺失或时序问题可以自然恢复。
 *
 * @param reference 引用状态与来源载体。
 * @returns 来源为草图实体时返回 true。
 */
export function isSketchSourceRebuildableReferenceRecord(
	reference: Pick<SketchEntityReferenceRecord, 'sourceDescriptor'>,
): boolean {
	return reference.sourceDescriptor.kind === 'sketch-entity';
}

/**
 * 解析来源引用实体 id。
 *
 * @param sourceDescriptor 来源描述。
 * @returns 规范化来源 id。
 */
export function resolveSourceRefEntityId(
	sourceDescriptor: ExternalGeometrySourceDescriptor,
): string {
	return resolveExternalGeometrySourceRefEntityId(sourceDescriptor);
}

/**
 * 按投影结果解析关系语义。
 *
 * @param projectionResult 投影结果。
 * @returns 关系语义。
 */
export function resolveProjectionRelationSemantics(
	projectionResult: ProjectionResult,
): SketchReferenceRelationSemantics {
	switch (projectionResult.projectionResultKind) {
		case 'line':
			return 'collinear';
		case 'circle':
		case 'ellipse':
			return 'coincident';
		case 'arc':
		case 'ellipse-arc':
		case 'parabola':
		case 'spline':
		case 'style-spline':
			return 'on-curve';
	}
}

/**
 * 将投影结果转为持久化重建载荷。
 *
 * @param projectionResult 投影结果。
 * @returns 重建载荷。
 */
export function toSketchEntityReferenceRebuildPayload(
	projectionResult: ProjectionResult,
): SketchEntityReferenceRebuildPayload {
	const endpointAnchorBindings = projectionResult.endpointAnchorBindings?.map(binding => ({
		sourceAnchorId: binding.sourceAnchorId,
		targetEndpoint: binding.targetEndpoint,
	}));
	const endpointPayload = endpointAnchorBindings && endpointAnchorBindings.length > 0
		? {endpointAnchorBindings}
		: {};
	switch (projectionResult.projectionResultKind) {
		case 'line':
			return {
				projectionResultKind: 'line',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'circle':
			return {
				projectionResultKind: 'circle',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'ellipse':
			return {
				projectionResultKind: 'ellipse',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'arc':
			return {
				projectionResultKind: 'arc',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'ellipse-arc':
			return {
				projectionResultKind: 'ellipse-arc',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'parabola':
			return {
				projectionResultKind: 'parabola',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'spline':
			return {
				projectionResultKind: 'spline',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
		case 'style-spline':
			return {
				projectionResultKind: 'style-spline',
				geometryPayload: projectionResult.geometryPayload,
				...endpointPayload,
			};
	}
}

/**
 * 从持久化引用记录恢复关系补偿所需的投影结果。
 *
 * Record 只保存可序列化的来源描述和几何载荷；补偿 relation 时不重新读取 Viewer，
 * 避免来源已经删除后无法恢复事务前的运行时关系。
 *
 * @param reference 引用记录。
 * @returns 可交给关系分发器重新挂载的投影结果。
 */
export function restoreProjectionResultFromReferenceRecord(
	reference: SketchEntityReferenceRecord,
): ProjectionResult {
	return {
		sourceDescriptor: structuredClone(reference.sourceDescriptor),
		sourceKind: reference.sourceKind,
		projectionResultKind: reference.rebuildPayload.projectionResultKind,
		geometryPayload: structuredClone(reference.rebuildPayload.geometryPayload),
		endpointAnchorBindings: reference.rebuildPayload.endpointAnchorBindings?.map(binding => ({
			sourceAnchorId: binding.sourceAnchorId,
			targetEndpoint: binding.targetEndpoint,
		})),
		diagnostics: [],
	} as ProjectionResult;
}

/**
 * 构建草图实体引用记录。
 *
 * @param input 构建输入。
 * @returns 规范化引用记录。
 */
export function buildSketchEntityReferenceRecord(input: {
	referenceId: string;
	projectionResult: ProjectionResult;
	targetSketchId: string;
	targetEntityId: string;
	sourceDisplayLabel?: string;
	/** 用户原始选择意图快照。*/
	selectionIntent?: SketchEntityReferenceSelectionIntentRecordSnapshot;
	/** topology face/loop 边界集合成员状态。 */
	boundaryMemberState?: SketchEntityReferenceBoundaryMemberState;
	projectionState?: SketchEntityReferenceProjectionState;
}): SketchEntityReferenceRecord {
	return {
		referenceId: input.referenceId,
		sourceDescriptor: input.projectionResult.sourceDescriptor,
		targetSketchId: input.targetSketchId,
		targetEntityId: input.targetEntityId,
		sourceDisplayLabel: input.sourceDisplayLabel,
		selectionIntent: input.selectionIntent,
		boundaryMemberState: input.boundaryMemberState,
		sourceKind: input.projectionResult.sourceKind,
		projectionResultKind: input.projectionResult.projectionResultKind,
		relationSemantics: resolveProjectionRelationSemantics(input.projectionResult),
		rebuildPayload: toSketchEntityReferenceRebuildPayload(input.projectionResult),
		projectionState: input.projectionState,
		status: 'active',
		referenceMode: 'sketch-entity-reference',
	};
}
